import React from 'react';
import PropTypes from 'prop-types';
import { Typography, Button } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { useTranslation } from 'react-i18next';
import { DateTime } from 'luxon';
import { EventIcon, AnimatedCheckMark } from 'components/elements/Icons';
import { dateUtils } from 'utils';
import { useBookAppointmentActions } from 'hooks';
import ProgressSubtitle from './ProgressSubtitle';
import Styles from './styles';

const useStyles = makeStyles(Styles);

function BookAppointmentConfirmation({ onConfirm, healthcareService, provider, date, startTime, endTime, location, timezone, reasonForVisit, onClickBack, isConfirmed }) {
  const classes = useStyles();
  const { t } = useTranslation('labels');
  const { bookAppointment } = useBookAppointmentActions();

  const confirmAppointment = () => {
    bookAppointment({
      providerAccountId: provider.id,
      healthcareServiceId: healthcareService.id,
      startDateTime: startTime,
      endDateTime: endTime,
      locationId: location.value,
      reasonForVisit,
      timezone,
    }).then(() => {
      onConfirm();
    });
  };

  const header = t('once_confirmed_this_appointment_will_be_added_to_your_upcoming_appointments');
  const defaultHeaderStr = header.split(' ').slice(0, -2).join(' ');
  const italicizedHeaderStr = header.split(' ').splice(-2).join(' ');

  return (
    <div className={classes.confirmationWrapper}>
      <div className={classes.headInfo}>
        <Typography variant="h6" component="span">
          {defaultHeaderStr}
        </Typography>{' '}
        <Typography sx={{ fontStyle: 'italic', textTransform: 'capitalize' }} variant="h6" component="span">
          {italicizedHeaderStr}
        </Typography>
      </div>
      <div className={classes.confirmationSubtitle}>
        <ProgressSubtitle>
          <>
            <Typography sx={{ fontWeight: 'bold' }} variant="h6" component="span">
              {healthcareService.name}
            </Typography>{' '}
            <Typography variant="h6" component="span">
              {t('with')}
            </Typography>{' '}
            <Typography sx={{ fontWeight: 'bold' }} variant="h6" component="span">
              {provider?.name}
            </Typography>{' '}
            <Typography variant="h6" component="span">
              {t('on')}
            </Typography>{' '}
            <Typography sx={{ fontWeight: 'bold' }} variant="h6" component="span">
              {`${dateUtils.fullFormatDateTimeString(date)}, ${dateUtils.formatTimeString(startTime, timezone)}`}
            </Typography>{' '}
            <Typography variant="h6" component="span">
              {t('to')}
            </Typography>{' '}
            <Typography sx={{ fontWeight: 'bold' }} variant="h6" component="span">
              {dateUtils.formatTimeString(endTime, timezone)}
            </Typography>{' '}
            <Typography variant="h6" component="span">
              {t('at')}
            </Typography>{' '}
            <Typography sx={{ fontWeight: 'bold' }} variant="h6" component="span">
              {location.label}
            </Typography>
          </>
        </ProgressSubtitle>
      </div>
      {isConfirmed ? (
        <AnimatedCheckMark classes={classes.confirmationIcon} />
      ) : (
        <div className={classes.controlButtons}>
          <Button variant="outlined" color="primary" size="large" onClick={onClickBack}>
            {t('back')}
          </Button>
          <Button className={classes.confirmButton} type="submit" variant="contained" color="primary" size="large" onClick={confirmAppointment}>
            <EventIcon classes={classes.confirmEventIcon} />
            {t('book_appointment')}
          </Button>
        </div>
      )}
    </div>
  );
}

BookAppointmentConfirmation.propTypes = {
  onClickBack: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  provider: PropTypes.objectOf(PropTypes.any),
  date: PropTypes.instanceOf(DateTime),
  isConfirmed: PropTypes.bool.isRequired,
  startTime: PropTypes.string,
  endTime: PropTypes.string,
  reasonForVisit: PropTypes.string,
  timezone: PropTypes.string,
  healthcareService: PropTypes.objectOf(PropTypes.any),
  location: PropTypes.objectOf(PropTypes.any),
};

BookAppointmentConfirmation.defaultProps = {
  provider: {},
  date: null,
  healthcareService: {},
  location: {},
  startTime: '',
  endTime: '',
  reasonForVisit: '',
  timezone: null,
};

export default BookAppointmentConfirmation;
